import type {
  GuestEventFeedbackForm,
  SubmitEventFeedbackRequest,
  SubmitEventFeedbackResponse,
} from '@mds/shared/types/event-feedback';

import { api } from '@/lib/api';

/**
 * The emailed-link feedback endpoints — see `backend/docs/event-feedback.md`.
 *
 * The token is the guest's only key: there is no sign-in, and an unknown,
 * cancelled or not-checked-in token is the same `404 EVENT_FEEDBACK_NOT_FOUND`.
 */

/**
 * The API path for one invitation — `/api/events/feedback/<token>` once the
 * client adds its base. Not the page: the emailed link opens the client route
 * of the same shape, and that page calls this.
 *
 * The token is base64url, so URL-safe already; encoding it costs nothing and
 * keeps a mangled link from changing the path.
 */
const feedbackPath = (token: string) => `events/feedback/${encodeURIComponent(token)}`;

/** The event, the two standard fields, the custom questions, and `alreadySubmitted`. */
export const getFeedbackForm = (token: string): Promise<GuestEventFeedbackForm> =>
  api.get<{ data: GuestEventFeedbackForm }>(feedbackPath(token)).then((r) => r.data);

/** One response per invitation: a second send is `409 EVENT_FEEDBACK_ALREADY_SUBMITTED`. */
export const submitFeedback = (
  token: string,
  body: SubmitEventFeedbackRequest,
): Promise<SubmitEventFeedbackResponse> =>
  api
    .post<{ data: SubmitEventFeedbackResponse }>(feedbackPath(token), body)
    .then((r) => r.data);
